// @generated-imports
import { applyFiltersAndRender, continueLastGame, escapeHTML, findGameById, getCardImage, playGame, recomposeGames, scrollToCatalog, showToast, truncate } from './app.js';
import { ensureSessionServices, stopSessionServices } from './autosave.js';
import { BACKUP_EXPORT_EXCLUDE, exportBackup, idbCountAll, idbOpen, listEmulatorDatabases, openBackupModal } from './backup.js';
import { renderFolderBackupStatus } from './backupplus.js';
import { CONFIG, SETTINGS_DEFS, dom, state } from './core.js';
import { EMU_SRAM_SUFFIX, EMU_STATE_SUFFIX, basenameOf, stripExtension } from './datapanel.js';
import { applyUiScale } from './display.js';
import { isLayerOpen, layerClosed, layerOpened, topLayer } from './layers.js';
import { isLocalRomId } from './library.js';
import { toggleLiteMode } from './lite.js';
import { isOwnStateKey, splitProfileIdentity } from './profiles.js';
import { currentSession } from './session.js';
import { listAllStateKeys, openStatesPanel } from './states.js';
import { resetTimeline } from './timeline.js';
// @generated-imports
/* ===========================================================================
   V1.6.0 — PREFERÊNCIAS, SESSÃO DE JOGO E RECURSOS DE PLATAFORMA
   ======================================================================== */

/* --- Preferências (painel ⚙️) --- */

function loadSettings() {
  let saved = {};
  try {
    saved = JSON.parse(localStorage.getItem(CONFIG.localStorageSettingsKey) || '{}') || {};
  } catch (_) { saved = {}; }
  state.settings = {};
  SETTINGS_DEFS.forEach((def) => {
    state.settings[def.id] = Object.prototype.hasOwnProperty.call(saved, def.id)
      ? saved[def.id]
      : def.def;
  });
}

function saveSettings() {
  try {
    localStorage.setItem(CONFIG.localStorageSettingsKey, JSON.stringify(state.settings));
  } catch (_) { /* preferências são melhor esforço */ }
}

function getSetting(id) {
  if (Object.prototype.hasOwnProperty.call(state.settings, id)) return state.settings[id];
  const def = SETTINGS_DEFS.find((entry) => entry.id === id);
  return def ? def.def : undefined;
}

function setSetting(id, value) {
  state.settings[id] = value;
  saveSettings();
}

function openSettingsModal() {
  renderSettingsList();
  dom.settingsModal.classList.remove('is-hidden');
  layerOpened('settings', dom.settingsClose);
}

function closeSettingsModal() {
  dom.settingsModal.classList.add('is-hidden');
  layerClosed('settings');
}

function renderSettingsList() {
  if (!dom.settingsList) return;
  const rows = SETTINGS_DEFS.map((def) => {
    const value = getSetting(def.id);
    if (def.options) {
      const label = (def.options.find((option) => option[0] === value) || def.options[0])[1];
      return `<div class="backup-row data-row">
        <span class="data-info">${escapeHTML(def.label)} <small>${escapeHTML(def.hint)}</small></span>
        <button class="mini-btn" type="button" data-cycle-setting="${def.id}" aria-label="${escapeHTML(def.label)}: ${escapeHTML(label)}">${escapeHTML(label)}</button>
      </div>`;
    }
    return `<div class="backup-row data-row">
      <span class="data-info">${escapeHTML(def.label)} <small>${escapeHTML(def.hint)}</small></span>
      <button class="mini-btn toggle-btn${value ? ' is-on' : ''}" type="button" role="switch" aria-checked="${value ? 'true' : 'false'}" data-toggle-setting="${def.id}">${value ? 'Ligado' : 'Desligado'}</button>
    </div>`;
  });
  // Modo leve mora aqui desde a v1.6 (antes era um botão solto na nav).
  rows.push(`<div class="backup-row data-row">
    <span class="data-info">Modo leve <small>Menos efeitos visuais, mais desempenho — bom para TVs e mini PCs.</small></span>
    <button class="mini-btn toggle-btn${state.liteMode ? ' is-on' : ''}" type="button" role="switch" aria-checked="${state.liteMode ? 'true' : 'false'}" data-toggle-lite="1">${state.liteMode ? 'Ligado' : 'Desligado'}</button>
  </div>`);
  dom.settingsList.innerHTML = rows.join('');
}

function handleSettingsClick(event) {
  const lite = event.target.closest('[data-toggle-lite]');
  if (lite) { toggleLiteMode(); renderSettingsList(); return; }

  const toggle = event.target.closest('[data-toggle-setting]');
  if (toggle) {
    const id = toggle.dataset.toggleSetting;
    setSetting(id, !getSetting(id));
    renderSettingsList();
    applySettingSideEffects(id);
    return;
  }

  const cycle = event.target.closest('[data-cycle-setting]');
  if (cycle) {
    const id = cycle.dataset.cycleSetting;
    const def = SETTINGS_DEFS.find((entry) => entry.id === id);
    if (!def || !def.options) return;
    const index = def.options.findIndex((option) => option[0] === getSetting(id));
    setSetting(id, def.options[(index + 1) % def.options.length][0]);
    renderSettingsList();
    applySettingSideEffects(id);
  }
}

/** Efeitos imediatos de uma preferência alterada durante o jogo. */
function applySettingSideEffects(id) {
  if (id === 'uiScale') applyUiScale();
  if (id === 'stateSlots' && isLayerOpen('states')) openStatesPanel();
  if (id === 'folderBackupDays') renderFolderBackupStatus();
  if (id === 'autosaveInterval' || id === 'rewind') {
    const session = currentSession();
    if (session) {
      stopSessionServices(session);
      if (id === 'rewind') resetTimeline(session);
      ensureSessionServices(session);
    }
  }
  if (id === 'wakeLock') {
    if (getSetting('wakeLock') && state.currentIframe) requestWakeLock();
    else releaseWakeLock();
  }
  if (id === 'padLegend') updatePadLegend();
  if (id === 'showcase') resetShowcaseTimer();
}

/* --- Wake Lock: a tela não apaga durante o jogo --- */

async function requestWakeLock() {
  if (!getSetting('wakeLock')) return;
  if (!navigator.wakeLock || typeof navigator.wakeLock.request !== 'function') return;
  if (state.wakeLock) return;
  try {
    state.wakeLock = await navigator.wakeLock.request('screen');
    // O navegador solta o lock ao trocar de aba: reencostamos ao voltar.
    state.wakeLock.addEventListener('release', () => { state.wakeLock = null; });
  } catch (_) { /* negado (bateria baixa, política) — segue sem */ }
}

function releaseWakeLock() {
  if (!state.wakeLock) return;
  try { state.wakeLock.release(); } catch (_) { /* noop */ }
  state.wakeLock = null;
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && state.currentIframe) requestWakeLock();
});

/* --- Modo de jogo: tela cheia, orientação e gestos do navegador --- */

function isTouchDevice() {
  return window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
}

function shouldAutoFullscreen() {
  const mode = getSetting('autoFullscreen');
  if (mode === 'always') return true;
  if (mode === 'never') return false;
  // 'auto': celular (tela pequena de toque) ou TV (tela grande com controle).
  const pads = navigator.getGamepads ? [...navigator.getGamepads()].filter(Boolean) : [];
  const bigScreenWithPad = window.innerWidth >= 1280 && pads.length > 0;
  return isTouchDevice() ? window.innerWidth < 900 : bigScreenWithPad;
}

async function enterPlayMode() {
  requestWakeLock();
  // Bloqueia pull-to-refresh e zoom por duplo-toque enquanto o jogo roda.
  document.body.classList.add('is-immersive');

  if (isTouchDevice() && getSetting('lockLandscape') && screen.orientation
    && typeof screen.orientation.lock === 'function') {
    // A trava só é aceita em tela cheia na maioria dos navegadores — por
    // isso vem depois do requestFullscreen abaixo.
    try {
      if (shouldAutoFullscreen()) await enterFullscreenSafe();
      await screen.orientation.lock('landscape');
    } catch (_) { /* desktop e iOS recusam — sem problema */ }
    return;
  }
  if (shouldAutoFullscreen()) {
    try { await enterFullscreenSafe(); } catch (_) { /* precisa de gesto — ok */ }
  }
}

function exitPlayMode() {
  releaseWakeLock();
  document.body.classList.remove('is-immersive');
  try {
    if (screen.orientation && typeof screen.orientation.unlock === 'function') screen.orientation.unlock();
  } catch (_) { /* noop */ }
  if (document.fullscreenElement) {
    try { document.exitFullscreen(); } catch (_) { /* noop */ }
  }
}

function enterFullscreenSafe() {
  const target = dom.playerSection || document.documentElement;
  if (document.fullscreenElement) return Promise.resolve();
  if (typeof target.requestFullscreen === 'function') return target.requestFullscreen();
  if (typeof target.webkitRequestFullscreen === 'function') {
    target.webkitRequestFullscreen();
    return Promise.resolve();
  }
  return Promise.resolve();
}

/* --- Tempo de jogo por título --- */

function loadPlayTime() {
  try {
    state.playTime = JSON.parse(localStorage.getItem(CONFIG.localStoragePlayTimeKey) || '{}') || {};
  } catch (_) { state.playTime = {}; }
}

function getPlayTime(id) {
  return Number(state.playTime[id]) || 0;
}

function startPlaySession(id) {
  endPlaySession();
  state.sessionGameId = id;
  state.sessionStartedAt = Date.now();
}

/** Fecha a sessão e acumula os segundos. Sessões de menos de 5s (troca
 *  rápida, ROM que não engatou) não contam. */
function endPlaySession() {
  if (!state.sessionStartedAt || !state.sessionGameId) return;
  const seconds = Math.round((Date.now() - state.sessionStartedAt) / 1000);
  state.sessionStartedAt = 0;
  const id = state.sessionGameId;
  state.sessionGameId = '';
  if (seconds < 5) return;
  state.playTime[id] = getPlayTime(id) + seconds;
  try {
    localStorage.setItem(CONFIG.localStoragePlayTimeKey, JSON.stringify(state.playTime));
  } catch (_) { /* noop */ }
}

// Fechar a aba no meio do jogo também contabiliza.
window.addEventListener('pagehide', endPlaySession);

function formatPlayTime(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h${String(minutes % 60).padStart(2, '0')}`;
}

/* --- Índice de saves: selo 💾 nos cards e filtro "Com save" --- */

/** Varre os bancos de states/SRAM e monta um índice por nome de arquivo e
 *  título — o mesmo casamento usado no painel de dados (v1.5). */
async function refreshSaveIndex() {
  const index = new Map();
  try {
    const databases = await listEmulatorDatabases();
    for (const meta of databases) {
      if (BACKUP_EXPORT_EXCLUDE.test(meta.name)) continue; // cache não é save
      // eslint-disable-next-line no-await-in-loop
      const db = await idbOpen(meta.name);
      for (const storeName of [...db.objectStoreNames]) {
        // eslint-disable-next-line no-await-in-loop
        const keys = await idbAllKeys(db, storeName);
        keys.forEach((key) => {
          const base = basenameOf(key);
          if (!EMU_STATE_SUFFIX.test(base) && !EMU_SRAM_SUFFIX.test(base)) return;
          const stem = base.replace(EMU_STATE_SUFFIX, '').replace(EMU_SRAM_SUFFIX, '');
          index.set(stem.toLowerCase(), true);
          index.set(stripExtension(stem).toLowerCase(), true);
        });
      }
      db.close();
    }
  } catch (_) { /* sem índice, o selo simplesmente não aparece */ }
  // Estados do próprio portal (emutab-states): id = "[<perfil>~]<gameId>|<core>:<slot>".
  // Só os do perfil ativo contam para o selo 💾.
  try {
    const keys = await listAllStateKeys();
    keys.filter(isOwnStateKey).forEach((key) => {
      const gameId = splitProfileIdentity(key).rest.split('|')[0];
      if (gameId) index.set(gameId.toLowerCase(), true);
    });
  } catch (_) { /* noop */ }
  state.saveIndex = index;
  updateSaveCountBadge();
}

/** Chaves de uma store sem carregar valores. */
function idbAllKeys(db, storeName) {
  return new Promise((resolve, reject) => {
    const request = db.transaction(storeName, 'readonly').objectStore(storeName).getAllKeys();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

function hasSaveFor(game) {
  if (!game || !state.saveIndex.size) return false;
  const candidates = [game.id, game.title, basenameOf(game.romUrl || ''), stripExtension(basenameOf(game.romUrl || ''))];
  if (game.fileName) {
    candidates.push(game.fileName, stripExtension(game.fileName));
  }
  return candidates.some((name) => name && state.saveIndex.has(String(name).toLowerCase()));
}

function updateSaveCountBadge() {
  if (!dom.saveCountBadge) return;
  const total = state.roms.filter((game) => hasSaveFor(game)).length;
  dom.saveCountBadge.textContent = String(total);
  dom.saveCountBadge.classList.toggle('is-hidden', total === 0);
  if (dom.withSaveToggle) dom.withSaveToggle.parentElement.classList.toggle('is-disabled', total === 0);
}

/* --- Save states pelo portal (botões do header + autosave) --- */

function setStateButtonsVisible(visible) {
  if (dom.saveStateBtn) dom.saveStateBtn.classList.toggle('is-hidden', !visible);
  if (dom.loadStateBtn) dom.loadStateBtn.classList.toggle('is-hidden', !visible);
  if (dom.statesBtn) dom.statesBtn.classList.toggle('is-hidden', !visible);
}



/* --- CTA do hero, aleatório e compartilhar --- */

function updateHeroCta() {
  if (!dom.heroPlayLabel) return;
  const lastGameId = localStorage.getItem(CONFIG.localStorageLastGameKey);
  const game = lastGameId
    ? (isLocalRomId(lastGameId) ? state.localRoms.get(lastGameId) : findGameById(lastGameId))
    : null;
  if (game) {
    dom.heroPlayLabel.textContent = `Continuar: ${truncate(game.title, 24)}`;
    dom.heroPlayBtn.querySelector('.btn-icon').textContent = '▶';
    dom.heroPlayBtn.dataset.action = 'continue';
  } else {
    dom.heroPlayLabel.textContent = 'Jogar um aleatório';
    dom.heroPlayBtn.querySelector('.btn-icon').textContent = '🎲';
    dom.heroPlayBtn.dataset.action = 'random';
  }
}

function heroPlayAction() {
  if (dom.heroPlayBtn.dataset.action === 'continue') continueLastGame();
  else playRandomGame();
}

function playRandomGame() {
  // Sorteia SÓ dentro do filtro atual — filtro vazio não vira "catálogo
  // inteiro" em silêncio. Evita repetir o último sorteado, exceto quando
  // ele é o único candidato.
  if (!state.bootReady) { showToast('Carregando o catálogo…'); return; }
  const visible = state.filteredRoms;
  if (!visible.length) {
    showToast('Nenhum jogo neste filtro para sortear — ajuste os filtros ou limpe-os.');
    return;
  }
  const pool = visible.length > 1
    ? visible.filter((game) => game.id !== state.lastRandomId)
    : visible;
  const game = pool[Math.floor(Math.random() * pool.length)];
  state.lastRandomId = game.id;
  showToast(`🎲 Sorteado: ${game.title}`);
  playGame(game);
}

async function shareCurrentGame() {
  const game = state.modalGame;
  if (!game) return;
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set('game', game.id);
  const link = url.toString();
  const shareData = { title: `EmuTab — ${game.title}`, text: `Jogue ${game.title} direto no navegador`, url: link };
  if (navigator.share) {
    try { await navigator.share(shareData); return; } catch (_) { /* cancelado: cai no copiar */ }
  }
  try {
    await navigator.clipboard.writeText(link);
    showToast('Link do jogo copiado.');
  } catch (_) {
    showToast(link);
  }
}

/* --- Instalação do PWA --- */

function initInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    state.installPrompt = event;
    if (dom.installBtn) dom.installBtn.classList.remove('is-hidden');
  });
  window.addEventListener('appinstalled', () => {
    state.installPrompt = null;
    if (dom.installBtn) dom.installBtn.classList.add('is-hidden');
    showToast('EmuTab instalado. Abra pelo ícone na tela inicial.');
  });
}

async function promptInstall() {
  if (!state.installPrompt) return;
  state.installPrompt.prompt();
  try { await state.installPrompt.userChoice; } catch (_) { /* noop */ }
  state.installPrompt = null;
  if (dom.installBtn) dom.installBtn.classList.add('is-hidden');
}

/* --- Ações de lançamento (atalhos do manifest e deep-links) --- */

function handleLaunchAction() {
  const params = new URLSearchParams(window.location.search);
  const action = params.get('action');
  const filter = params.get('filter');
  if (filter === 'favorites' && dom.favoritesToggle) {
    dom.favoritesToggle.checked = true;
    state.favoritesOnly = true;
    applyFiltersAndRender();
    scrollToCatalog();
  }
  if (action === 'continue') continueLastGame();
  if (action === 'random') playRandomGame();
  if (action || filter) {
    // Limpa a URL para o atalho não repetir a ação a cada refresh.
    const url = new URL(window.location.href);
    url.searchParams.delete('action');
    url.searchParams.delete('filter');
    window.history.replaceState({}, '', url.toString());
  }
}

/* --- Legenda de botões do controle --- */

function padLegendItems() {
  const layer = topLayer();
  if (layer) {
    return layer.legend || [['A', 'Acionar'], ['B', 'Fechar'], ['↕', 'Navegar']];
  }
  if (state.session) {
    return [['Select+Start', 'Menu (toque) · Desligar (segurar 1,5 s)']];
  }
  return [
    ['A', 'Jogar'], ['B', 'Voltar'], ['X', 'Detalhes'], ['Y', '♥'],
    ['LB/RB', 'Sistema'], ['LT', 'Gênero'], ['RT', 'Ordem'], ['Start', 'Continuar'],
  ];
}

function updatePadLegend() {
  if (!dom.padLegend) return;
  const pads = navigator.getGamepads ? [...navigator.getGamepads()].filter(Boolean) : [];
  const visible = pads.length > 0 && getSetting('padLegend') && !state.liteMode;
  dom.padLegend.classList.toggle('is-hidden', !visible);
  if (!visible) return;
  const items = padLegendItems();
  const signature = items.map((item) => item.join(':')).join('|');
  if (signature === state.padLegendContext) return; // evita repintar a cada frame
  state.padLegendContext = signature;
  dom.padLegend.innerHTML = items
    .map(([key, label]) => `<span class="pad-key"><b>${escapeHTML(key)}</b> ${escapeHTML(label)}</span>`)
    .join('');
}

/** Avança/retrocede um <select> (usado pelos ombros do controle). */
function cycleSelect(select, direction) {
  if (!select || !select.options.length) return;
  const next = (select.selectedIndex + direction + select.options.length) % select.options.length;
  select.selectedIndex = next;
  select.dispatchEvent(new Event('change', { bubbles: true }));
  showToast(`${select.previousElementSibling ? select.previousElementSibling.textContent : 'Filtro'}: ${select.options[next].textContent}`);
}

/* --- Ajuda ("Como jogar") --- */

const HELP_KEYBOARD = [
  ['Setas', 'Direcional do controle'],
  ['Z', 'Botão A (ação principal)'],
  ['X', 'Botão B'],
  ['S', 'Botão X'],
  ['A', 'Botão Y'],
  ['Enter', 'Start'],
  ['Shift', 'Select'],
  ['Q / W', 'L / R (ombros)'],
  ['F2 / F4', 'Salvar / carregar estado'],
];

const HELP_GAMEPAD = [
  ['Direcional / analógico', 'Navegar e jogar'],
  ['A', 'Jogar o card em foco · confirmar'],
  ['B', 'Voltar · fechar'],
  ['X', 'Abrir detalhes do jogo'],
  ['Y', 'Favoritar o jogo em foco'],
  ['LB / RB', 'Trocar de sistema'],
  ['LT / RT', 'Trocar gênero / ordenação'],
  ['Start', 'Continuar último jogo'],
  ['Select + Start (toque)', 'Menu de pausa (também: botão Home)'],
  ['Select + Start (segurar 1,5 s)', 'Desligar o emulador'],
];

const HELP_CATALOG = [
  ['/', 'Focar a busca'],
  ['Setas', 'Mover entre os cards'],
  ['Enter', 'Jogar o card em foco'],
  ['Espaço', 'Abrir detalhes'],
  ['F', 'Favoritar'],
  ['?', 'Abrir esta ajuda'],
  ['Esc', 'Fechar janelas / limpar busca'],
];

const HELP_SAVES = [
  ['Menu de pausa', 'No jogo, aperte Select+Start rápido (ou Home) — ou o botão ☰ Menu. Salvar, carregar, voltar no tempo, avanço rápido e trocar jogadores pelo controle.'],
  ['Salvar estado', 'Botão 💾 no topo do player (ou F2) guarda o ponto exato no slot 1. ⏪ Estados tem até 10 slots com nome.'],
  ['Carregar', 'Botão ↩ (ou F4) volta ao último estado salvo.'],
  ['Automático', 'O progresso é gravado a cada 5 min (⚙️) e ao desligar. Se o aparelho reiniciar, o EmuTab oferece retomar.'],
  ['Outro aparelho', 'Em ⏪ Estados, "Enviar" gera um arquivo pequeno; no outro EmuTab, importe ou solte o arquivo na página.'],
  ['Save do cartucho', 'Jogos com bateria salvam sozinhos — aparece o selo 💾 no card.'],
  ['Ao desligar', 'Com "Salvar estado ao desligar" ligado, o Select+Start guarda o progresso antes de sair.'],
  ['Backup', 'O painel 💾 exporta tudo num arquivo e restaura em outra máquina.'],
];

function helpRowsHtml(rows) {
  return rows.map(([key, label]) => `<div class="backup-row data-row">
    <span class="data-info">${escapeHTML(label)}</span>
    <kbd class="help-key">${escapeHTML(key)}</kbd>
  </div>`).join('');
}

function openHelpModal() {
  if (!dom.helpModal) return;
  state.helpLastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  dom.helpKeyboard.innerHTML = helpRowsHtml(HELP_KEYBOARD);
  dom.helpGamepad.innerHTML = helpRowsHtml(HELP_GAMEPAD);
  dom.helpCatalog.innerHTML = helpRowsHtml(HELP_CATALOG);
  dom.helpSaves.innerHTML = HELP_SAVES.map(([title, text]) => `<div class="backup-row">
    <strong>${escapeHTML(title)}</strong> — ${escapeHTML(text)}
  </div>`).join('');
  dom.helpModal.classList.remove('is-hidden');
  layerOpened('help', dom.helpClose);
}

function closeHelpModal() {
  if (!dom.helpModal) return;
  dom.helpModal.classList.add('is-hidden');
  layerClosed('help');
}

/* --- Lembrete de backup --- */

function markBackupDone() {
  try { localStorage.setItem(CONFIG.localStorageLastBackupKey, String(Date.now())); } catch (_) { /* noop */ }
}

function lastBackupAt() {
  const raw = Number(localStorage.getItem(CONFIG.localStorageLastBackupKey) || 0);
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
}

async function maybeRemindBackup() {
  if (!getSetting('backupReminder')) return;
  const last = lastBackupAt();
  const days = last ? (Date.now() - last) / 86400000 : Infinity;
  if (days < CONFIG.backupReminderDays) return;
  // Só incomoda quem tem o que perder.
  let hasSaves = false;
  try {
    const databases = await listEmulatorDatabases();
    for (const meta of databases) {
      if (BACKUP_EXPORT_EXCLUDE.test(meta.name)) continue;
      // eslint-disable-next-line no-await-in-loop
      if (await idbCountAll(meta.name) > 0) { hasSaves = true; break; }
    }
  } catch (_) { return; }
  if (!hasSaves) return;
  showToast(last
    ? `Faz ${Math.floor(days)} dias desde o último backup dos seus saves.`
    : 'Você tem saves guardados e nenhum backup exportado ainda.',
  { action: { label: 'Exportar agora', handler: () => { openBackupModal(); exportBackup(); } } });
}

/* --- Modo vitrine (slideshow quando ocioso) --- */

function initShowcase() {
  ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach((type) => {
    document.addEventListener(type, handleShowcaseActivity, { passive: true });
  });
  resetShowcaseTimer();
}

function handleShowcaseActivity() {
  if (dom.showcase && !dom.showcase.classList.contains('is-hidden')) stopShowcase();
  resetShowcaseTimer();
}

function resetShowcaseTimer() {
  window.clearTimeout(state.showcaseTimer);
  if (!getSetting('showcase')) return;
  state.showcaseTimer = window.setTimeout(startShowcase, CONFIG.showcaseIdleMs);
}

function startShowcase() {
  // Nunca durante o jogo, com modal aberto ou sem catálogo carregado.
  if (state.currentIframe || document.body.classList.contains('has-modal') || !state.roms.length) {
    resetShowcaseTimer();
    return;
  }
  dom.showcase.classList.remove('is-hidden');
  dom.showcase.setAttribute('aria-hidden', 'false');
  showcaseNext();
}

function showcaseNext() {
  if (!dom.showcase || dom.showcase.classList.contains('is-hidden')) return;
  const pool = state.roms.filter((game) => getCardImage(game));
  if (!pool.length) { stopShowcase(); return; }
  const game = pool[state.showcaseIndex % pool.length];
  state.showcaseIndex += 1;
  dom.showcaseArt.src = getCardImage(game);
  dom.showcaseArt.alt = `Capa de ${game.title}`;
  dom.showcaseTitle.textContent = game.title;
  dom.showcaseMeta.textContent = `${game.system} • ${game.year}`;
  state.showcaseTimer = window.setTimeout(showcaseNext, 7000);
}

function stopShowcase() {
  if (!dom.showcase) return;
  window.clearTimeout(state.showcaseTimer);
  dom.showcase.classList.add('is-hidden');
  dom.showcase.setAttribute('aria-hidden', 'true');
  dom.showcaseArt.src = '';
}

/* --- Erro global visível (na TV ninguém abre o console) --- */

function initGlobalErrorHandler() {
  const report = (message, detail) => {
    const info = [
      `EmuTab ${CONFIG.appVersion}`,
      message,
      detail,
      navigator.userAgent,
    ].filter(Boolean).join('\n');
    showToast('Algo deu errado no portal.', {
      action: {
        label: 'Copiar detalhes',
        handler: () => {
          navigator.clipboard.writeText(info)
            .then(() => showToast('Detalhes copiados.'))
            .catch(() => showToast(info));
        },
      },
    });
  };

  window.addEventListener('error', (event) => {
    if (!event.message) return;
    report(event.message, event.filename ? `${event.filename}:${event.lineno}` : '');
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    report('Promessa rejeitada', reason && reason.message ? reason.message : String(reason || ''));
  });
}


/* --- ROMs locais como cards do catálogo (v1.6) ---------------------------
   Antes, uma ROM solta na página só reaparecia como chip de recente e na
   lista do painel de saves — "onde foi parar?". Agora ela vira card num
   sistema virtual "📼 Minhas ROMs", com as mesmas ações dos demais. */

const LOCAL_SYSTEM_LABEL = '📼 Minhas ROMs';


/** Reconstrói a fatia local do catálogo (idempotente: pode ser chamada
 *  sempre que a biblioteca mudar). */
function syncLocalRomsIntoCatalog() {
  recomposeGames();
}

// @generated-exports
export { HELP_CATALOG, HELP_GAMEPAD, HELP_KEYBOARD, HELP_SAVES, LOCAL_SYSTEM_LABEL, applySettingSideEffects, closeHelpModal, closeSettingsModal, cycleSelect, endPlaySession, enterFullscreenSafe, enterPlayMode, exitPlayMode, formatPlayTime, getPlayTime, getSetting, handleLaunchAction, handleSettingsClick, handleShowcaseActivity, hasSaveFor, helpRowsHtml, heroPlayAction, idbAllKeys, initGlobalErrorHandler, initInstallPrompt, initShowcase, isTouchDevice, lastBackupAt, loadPlayTime, loadSettings, markBackupDone, maybeRemindBackup, openHelpModal, openSettingsModal, padLegendItems, playRandomGame, promptInstall, refreshSaveIndex, releaseWakeLock, renderSettingsList, requestWakeLock, resetShowcaseTimer, saveSettings, setSetting, setStateButtonsVisible, shareCurrentGame, shouldAutoFullscreen, showcaseNext, startPlaySession, startShowcase, stopShowcase, syncLocalRomsIntoCatalog, updateHeroCta, updatePadLegend, updateSaveCountBadge };
// @generated-exports
